import { ApiError, OFFLINE, type Api, type AuditRecordRef } from "../api/client.js";
import type { AuditVerdict, Evidence, Health, MandateView, PolicyEvaluated } from "../api/types.js";
import type { FixtureDrafter } from "../adapters/drafter.js";
import { clock } from "../lib/format.js";
import { el } from "../lib/dom.js";
import { attacksView, chainView, identityProofView, judgeHome, judgeTopbar, proofView, type AttackRow } from "../ui/judge.js";
import { runAttacks, tally } from "./attacks.js";
import { b64url, canonical, hex, sha256 } from "./jcs.js";
import { chainRows, modelsRows, proofModel, refreshProof, turnProof, type Panel, type Recheck, type RefreshProof, type RefusedTurnProof, type SpeechFacts } from "./judge-model.js";
import { runPanel, schedPanel, tracesOf, type RunPanel } from "./judge-sched.js";

/**
 * Judge mode's controller (UX spec v3 §6.14): the surface under Me when the switch
 * is on, and the three pushed screens — Proof, Attacks, Chain. It reads what any
 * signed-in customer may read about their own bundles (`/evidence`, `/v1/audit/
 * verify`) and drives the demo-only routes a session holds while `DEMO_MODE=1`
 * (the tamper, the clock — ask i). It grants nothing over money: every attack goes
 * through the same public routes, and what it drafts it cancels.
 */
export interface JudgeDeps {
  readonly api: Api;
  readonly drafter: FixtureDrafter;
  /** Put a pushed screen up (top bar, body, the tab bar). */
  readonly mount: (parts: readonly HTMLElement[]) => void;
  readonly dock: () => HTMLElement;
  /** The chat's last measured stages, as the bank reported them (ask g). */
  readonly timing: () => string | undefined;
  readonly onSignedOut: () => void;
  /**
   * DCTA-176 (F49): the identity check this session — the turn the gate refused (the chat kept it) and the refresh that
   * ran (the flow kept it). Either, both or none; with none, judge mode shows no identity-check Proof.
   */
  readonly identity?: () => { readonly turn?: RefusedTurnProof | undefined; readonly refresh?: RefreshProof | undefined };
  /** DCTA-188 Change 4: the session's one-shot faulty-drafter switch (app/demo-fault.ts) — the chat reads the same one. */
  readonly demoFault?: { readonly armed: boolean; set(on: boolean): void } | undefined;
  /** P1-9 (DCTA-192): this session's speech (engine, voice, Read aloud) for Models and timing; none: no Speech row. */
  readonly speech?: () => SpeechFacts;
  /**
   * DCTA-236 W2: judge mode moved money with no signature in the chat — a run of due payments, or the clock's fast-forward
   * settling held payments — so the screens that show money (Home, an account's page, Invest) read again at once (DCTA-231
   * V2's hook): the customer never meets the old balance there.
   */
  readonly onBankChanged?: () => void;
}

export class JudgeMode {
  readonly #d: JudgeDeps;
  #attacks: { rows: AttackRow[]; ledger: string } | undefined;
  /** QA D7: the last run could not finish — what stopped it, said on the panel with Run all four again. */
  #attackError: string | undefined;
  #running = false;
  /** DCTA-209 (F62): each press of Run due payments now, newest first — kept while the session lasts. */
  readonly #runs: RunPanel[] = [];
  #runningDue = false;

  constructor(deps: JudgeDeps) {
    this.#d = deps;
  }

  /** The newest bundle this customer signed, from the ledger (the proof's subject). */
  async #lastBundle(): Promise<string | undefined> {
    const l = await this.#d.api.ledgerEntries(20);
    return l.entries.find((e) => e.bundle_id !== undefined)?.bundle_id;
  }

  /** 12 on: the surface under Me's list. */
  async home(slot: HTMLElement, back: () => void): Promise<void> {
    const api = this.#d.api;
    try {
      const [health, lastId, chain, demoUp, mandates] = await Promise.all([
        api.health().catch((): Health => ({ status: "unknown" })),
        this.#lastBundle(),
        api.verifyChain().catch(() => undefined),
        // DCTA-188: the faulty-drafter switch only where the bank's demo routes answer (a 404 elsewhere: no switch).
        this.#d.demoFault === undefined ? Promise.resolve(false) : api.demoState().then(() => true, () => false),
        // DCTA-209 (F62): route 1 for the Scheduled payments panel; a bank without it (404) draws no panel.
        (async (): Promise<readonly MandateView[]> => (await api.mandates()).mandates)().catch(() => undefined),
      ]);
      const demo = demoUp ? this.#d.demoFault : undefined;
      const [evidence, own] = lastId === undefined ? [undefined, undefined] : await Promise.all([api.evidence(lastId).catch(() => undefined), api.verifyAudit(lastId).catch(() => undefined)]);
      const signedAt = evidence?.signed.signature[0]?.at;
      slot.replaceChildren(
        judgeHome(
          {
            ...(lastId === undefined || evidence === undefined
              ? {}
              : {
                  last: {
                    id: lastId,
                    steps: evidence.shown.payload.steps.length,
                    signedAt: signedAt === undefined ? "" : clockSeconds(signedAt),
                    ...(own === undefined ? {} : { chain: own.ok }),
                  },
                }),
            ...(this.#attacks === undefined
              ? {}
              : { attacks: tally(this.#attacks.rows) }),
            ...(chain === undefined ? {} : { chain: { ok: chain.ok, total: chain.records_total } }),
            models: modelsRows(evidence, health, this.#d.timing(), this.#d.speech?.()),
            clock: {
              offsetSeconds: health.clock_offset_seconds ?? 0,
              simulated: health.simulated_time === true,
              ...(health.now === undefined ? {} : { now: health.now }),
            },
            ...(demo === undefined ? {} : { demoFault: { on: demo.armed } }),
            ...(mandates === undefined ? {} : { scheduled: { panel: schedPanel(mandates), runs: [...this.#runs] } }),
          },
          {
            onProof: () => {
              if (lastId !== undefined) void this.proof(lastId, back);
            },
            ...(this.#identity() === undefined ? {} : { onIdentity: () => void this.identity(back) }),
            onAttacks: () => void this.attacks(back),
            onChain: () => void this.chain(back),
            ...(demo === undefined ? {} : { onDemoFault: (on: boolean) => demo.set(on) }),
            onRunDue: (done) => void this.#runDue(slot, back, done),
            onFastForward: (done) => {
              const next = (health.clock_offset_seconds ?? 0) + 12 * 3600;
              api
                .demoClock(next)
                .then(() => {
                  // DCTA-236 W2: the bank settled every hold the new time reached — the screens that show money read again.
                  // MUTATION TARGET: without it a held transfer that went out still shows on Home.
                  this.#d.onBankChanged?.();
                  return this.home(slot, back);
                })
                .catch((error: unknown) => done(error instanceof ApiError ? (error.serverMessage ?? error.reasonCode) : "The clock did not move."));
            },
          },
        ),
      );
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") this.#d.onSignedOut();
    }
  }

  /**
   * DCTA-209 (F62): route 4 — the demo world's clock moves to the earliest due payment and what is due runs; then each
   * payment's record: its schedule (route 1), its policy trace (the signing bundle's evidence), its place in the chain
   * (the caller's own records) and the chain's verdict. One panel per press, newest first; the home is drawn again (the
   * Clock panel shows the new offset).
   */
  async #runDue(slot: HTMLElement, back: () => void, done: (error?: string) => void): Promise<void> {
    if (this.#runningDue) return;
    this.#runningDue = true;
    const api = this.#d.api;
    try {
      const r = await api.runDue();
      // DCTA-236 W2: the bank answered — money may have moved: Home and the rest read again now, beside this panel's reads.
      // MUTATION TARGET: without it Home's first drawing after the run is the old balance (prod-verify 15 Oct, W2).
      this.#d.onBankChanged?.();
      // The records and the chain's verdict need nothing from route 1: read beside it and the evidence.
      const recorded = Promise.all([ownRecords(api), api.verifyChain().catch(() => undefined)]);
      const list = await api.mandates().then((x) => x.mandates, () => [] as readonly MandateView[]);
      const bundles = [...new Set(r.ran.map((o) => list.find((v) => v.mandate_id === o.mandate_id)?.bundle_id).filter((b): b is string => b !== undefined))];
      const evidences = await Promise.all(bundles.map((b) => api.evidence(b).catch(() => undefined)));
      const traces = new Map<string, PolicyEvaluated>();
      for (const e of evidences) for (const [k, v] of tracesOf(e)) traces.set(k, v);
      const [records, chain] = await recorded;
      const seqOf = (ref: string): string | undefined => records.find((x) => x.audit_ref === ref)?.seq;
      this.#runs.unshift(runPanel(r, { mandates: list, traces, seqOf, ...(chain === undefined ? {} : { chainOk: chain.ok }) }));
      await this.home(slot, back);
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      done(error instanceof ApiError ? (error.serverMessage ?? `The bank answered ${error.reasonCode}.`) : "The payments did not run.");
    } finally {
      this.#runningDue = false;
    }
  }

  #push(title: string, view: HTMLElement, back: () => void, backLabel?: string): void {
    const body = el("div", { class: "body" }, [view, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    this.#d.mount([judgeTopbar(title, back, backLabel), body, this.#d.dock()]);
  }

  /** 12 proof: said → … → recorded, the payload re-hashed in this browser. */
  async proof(bundleId: string, back: () => void, backLabel?: string): Promise<void> {
    const [evidence, own] = await Promise.all([this.#d.api.evidence(bundleId), this.#d.api.verifyAudit(bundleId).catch(() => undefined)]);
    const recheck = await recheckOf(evidence).catch(() => undefined);
    // DCTA-198: a kept clip, fetched on the tap and re-hashed in this browser (jcs.ts's SHA-256, as the payload's re-check).
    const onClip = async (audioId: string): Promise<{ bytes: Uint8Array; hex: string } | undefined> => {
      const bytes = await this.#d.api.utteranceAudio(audioId);
      return bytes === undefined ? undefined : { bytes, hex: hex(await sha256(bytes)) };
    };
    this.#push("Proof", proofView(bundleId, proofModel(evidence, own, recheck), recheck?.text, () => void this.chain(() => void this.proof(bundleId, back, backLabel)), onClip), back, backLabel);
  }

  #identity(): { readonly turn?: RefusedTurnProof | undefined; readonly refresh?: RefreshProof | undefined } | undefined {
    const id = this.#d.identity?.();
    return id === undefined || (id.turn === undefined && id.refresh === undefined) ? undefined : id;
  }

  /**
   * F49 (§2.5): the identity check's Proof, in the order it happened — the turn refused before any question, then the
   * refresh signed. The records by position (`GET /v1/audit/records`), the refresh's evidence, the chain's verdict.
   */
  async identity(back: () => void): Promise<void> {
    const id = this.#identity();
    if (id === undefined) return;
    const api = this.#d.api;
    const [records, chain, evidence] = await Promise.all([
      ownRecords(api),
      api.verifyChain().catch(() => undefined),
      id.refresh === undefined ? Promise.resolve(undefined) : api.evidence(id.refresh.bundleId).catch(() => undefined),
    ]);
    const panels: Panel[] = [
      ...(id.turn === undefined ? [] : [turnProof(id.turn, records, chain?.ok)]),
      ...(id.refresh === undefined || evidence === undefined ? [] : [refreshProof(id.refresh, evidence, records, chain?.ok)]),
    ];
    this.#push("Proof", identityProofView(panels), back);
  }

  /** 12 attacks: run once when opened, then again on request. */
  async attacks(back: () => void): Promise<void> {
    if (this.#attacks !== undefined) {
      this.#drawAttacks(back);
      return;
    }
    this.#attackError = undefined;
    this.#drawAttacks(back);
    await this.#run(back);
  }

  #drawAttacks(back: () => void): void {
    this.#push("Attacks", attacksView(this.#attacks?.rows, this.#attacks?.ledger, (done) => void this.#run(back).finally(done), this.#attackError), back);
  }

  /**
   * QA D7: the run ALWAYS ends on a drawn panel — the rows, or what stopped it with Run all four again. (It hung on
   * "running…" when an attack threw: there was a `finally` and no `catch`.)
   */
  async #run(back: () => void): Promise<void> {
    if (this.#running) return;
    this.#running = true;
    try {
      const lastId = await this.#lastBundle();
      const last = lastId === undefined ? undefined : await this.#d.api.evidence(lastId).catch(() => undefined);
      this.#attacks = await runAttacks(this.#d.api, this.#d.drafter, last);
      this.#attackError = undefined;
    } catch (error) {
      if (error instanceof ApiError && error.reasonCode === "AUTH_REQUIRED") {
        this.#d.onSignedOut();
        return;
      }
      this.#attacks = undefined;
      this.#attackError = attackFailure(error);
    } finally {
      this.#running = false;
    }
    this.#drawAttacks(back);
  }

  /** 12 chain: `GET /v1/audit/verify` as it answers; the demo tamper behind a question. */
  async chain(back: () => void): Promise<void> {
    const v: AuditVerdict = await this.#d.api.verifyChain();
    this.#push(
      "Chain",
      chainView(v.ok, chainRows(v), v.broken_link?.seq, {
        onVerify: (done) => void this.chain(back).finally(done),
        onTamper: () => void this.#tamper(back),
      }),
      back,
    );
  }

  /** The demo's closing beat: edit the last payment's displayed record, then verify again. */
  async #tamper(back: () => void): Promise<void> {
    const lastId = await this.#lastBundle();
    if (lastId === undefined) return;
    const e = await this.#d.api.evidence(lastId);
    const target = e.shown.displayed[0]?.audit_ref;
    if (target === undefined) return;
    await this.#d.api.tamper(target, "steps[1].amount.value", "8000.00").catch(() => undefined);
    await this.chain(back);
  }
}

/** QA D7: what stopped a run, in judge mode's words — the bank's reason code, or that no answer came. */
function attackFailure(error: unknown): string {
  if (error instanceof ApiError) return error.reasonCode === OFFLINE ? "No answer from the bank." : `The bank answered ${error.reasonCode}.`;
  return error instanceof Error && error.message !== "" ? error.message : "Something stopped the run.";
}

/** The customer's own records by position, page after page (at most ten pages of 200) — a long session's too. */
export async function ownRecords(api: Pick<Api, "auditRecords">): Promise<AuditRecordRef[]> {
  const all: AuditRecordRef[] = [];
  let from = 1;
  for (let page = 0; page < 10; page += 1) {
    const r = await api.auditRecords(from).catch(() => undefined);
    if (r === undefined) break;
    all.push(...r.records);
    if (r.next_from_seq === undefined) break;
    from = Number(r.next_from_seq);
  }
  return all;
}

async function recheckOf(e: Evidence): Promise<Recheck> {
  const text = canonical(e.shown.payload);
  const bytes = new TextEncoder().encode(text);
  const digest = await sha256(bytes);
  return { text, bytes: bytes.length, canonicalHex: hex(digest), challenge: b64url(digest) };
}

/** "21:41:00" — the judge register shows seconds. */
function clockSeconds(iso: string): string {
  const s = new Date(iso).getUTCSeconds();
  return `${clock(iso)}:${String(s).padStart(2, "0")}`;
}
